// src/views/PogeView.jsx
import React from 'react';
import { Shield, Zap, TrendingDown, Lock, ArrowRight } from 'lucide-react';
import { formatBalance } from '../utils/formatters';
import { CONFIG } from '../config/constants';

export const PogeView = ({
  isConnected,
  connectWallet,
  pogeBalance,
  pendingRewards,
  stakedTokens,
  emissionRate,
  rewardPoolBalance,
  pogeStats,
  setView,
}) => {
  const dailyEarnings = parseFloat(emissionRate || 0) * (stakedTokens?.length || 0) * 86400;

  const EMISSION_TIMELINE = [
    { day: 'Day 1',   base: '~10 POGE', with2x: '~10 POGE'    },
    { day: 'Day 180', base: '~6.25 POGE', with2x: '~9.375 POGE' },
    { day: 'Day 365', base: '~3.75 POGE', with2x: '~7.5 POGE'   },
    { day: 'Day 730', base: '~1 POGE',  with2x: '~2 POGE'     },
  ];

  const GUARANTEES = [
    { icon: Shield, title: 'No Admin Functions',   desc: 'The staking contract has zero admin functions. Cannot be paused, upgraded, or modified.' },
    { icon: Lock,   title: 'No Mint Function',     desc: 'Supply is fixed at 1 billion. Nobody can mint additional POGE after deployment.' },
    { icon: Zap,    title: 'Always Withdrawable',  desc: 'emergencyWithdraw() always returns your NFTs even if the reward pool runs dry.' },
    { icon: Shield, title: 'No Vesting, No Cliffs',desc: 'No team tokens. No vesting schedules. 100% of supply goes to stakers.' },
    { icon: TrendingDown, title: 'Verifiable On-Chain', desc: 'Emission rates, decay curves, and multipliers are public contract constants.' },
    { icon: Lock,   title: 'Censorship Resistant', desc: 'No admin can prevent claiming rewards or unstaking. The contract runs autonomously.' },
  ];

  return (
    <div>
      {/* PAGE TITLE */}
      <div className="flex items-center gap-4 mb-8">
        <h2 className="text-3xl md:text-5xl font-black text-white uppercase font-mono">
          Pogecoin
        </h2>
        <span className="bg-yellow-300 text-black font-black text-sm px-3 py-1 border-2 border-black font-mono">
          $POGE
        </span>
        <span className="bg-black text-green-400 font-black text-xs px-3 py-1 border-2 border-green-400 font-mono uppercase">
          Never Sold
        </span>
      </div>

      <div className="space-y-8">

        {/* PERSONAL STATS — only shown when connected */}
        {isConnected && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-yellow-300 text-black p-6 border-4 border-black shadow-[6px_6px_0_0_#000]">
              <div className="text-xs font-black uppercase font-mono opacity-60 mb-1">Your $POGE Balance</div>
              <div className="text-4xl font-black font-mono">{formatBalance(pogeBalance)}</div>
              <div className="text-sm font-bold mt-1 opacity-70">POGE</div>
            </div>
            <div className="bg-green-400 text-black p-6 border-4 border-black shadow-[6px_6px_0_0_#000]">
              <div className="text-xs font-black uppercase font-mono opacity-60 mb-1">Pending Rewards</div>
              <div className="text-4xl font-black font-mono">{parseFloat(pendingRewards || 0).toFixed(4)}</div>
              <div className="text-sm font-bold mt-1 opacity-70">POGE unclaimed</div>
            </div>
            <div className="bg-black text-white p-6 border-4 border-white shadow-[6px_6px_0_0_rgba(255,255,255,0.3)]">
              <div className="text-xs font-black uppercase font-mono text-green-400 mb-1">Daily Earnings</div>
              <div className="text-4xl font-black font-mono text-yellow-300">
                +{dailyEarnings.toFixed(2)}
              </div>
              <div className="text-sm font-bold mt-1 opacity-50">{stakedTokens?.length || 0} NFTs staked</div>
            </div>
          </div>
        )}

        {/* NOT CONNECTED PROMPT */}
        {!isConnected && (
          <div className="bg-black border-4 border-yellow-300 p-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <div className="text-yellow-300 font-black uppercase font-mono text-sm mb-1">Start Earning $POGE</div>
              <div className="text-white font-mono text-sm opacity-70">Connect your wallet to see your balance and pending rewards</div>
            </div>
            <button
              onClick={connectWallet}
              className="bg-yellow-300 text-black font-black px-8 py-3 border-4 border-black shadow-[4px_4px_0_0_#000] hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[6px_6px_0_0_#000] transition-all uppercase font-mono whitespace-nowrap flex items-center gap-2"
            >
              Connect Wallet <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* TOKEN STATS */}
        <div>
          <h3 className="text-xl font-black uppercase font-mono text-white border-b-4 border-white pb-2 mb-4">
            Token Facts
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Name',           value: 'Pogecoin',       sub: 'ERC-20'           },
              { label: 'Symbol',         value: '$POGE',          sub: 'Ethereum Mainnet' },
              { label: 'Total Supply',   value: '1,000,000,000',  sub: 'Fixed forever'    },
              { label: 'Allocation',     value: '100%',           sub: 'Staking rewards'  },
              { label: 'Public Sale',    value: 'None',           sub: 'Never sold'       },
              { label: 'Team Tokens',    value: 'None',           sub: 'Zero allocation'  },
              { label: 'Emission',       value: '264 Years',      sub: 'Decaying rate'    },
              { label: 'Multiplier',     value: '1x → 2x',        sub: 'Over 180 days'    },
            ].map(({ label, value, sub }) => (
              <div key={label} className="bg-black border-l-4 border-yellow-300 border-t border-r border-b border-white/10 p-4">
                <div className="text-xs text-white/40 uppercase font-mono letter-spacing-wide mb-1">{label}</div>
                <div className="text-lg font-black text-white font-mono">{value}</div>
                <div className="text-xs text-white/40 font-mono mt-0.5">{sub}</div>
              </div>
            ))}
          </div>
        </div>

        {/* CONTRACT ADDRESS */}
        <div className="bg-black border-4 border-green-400 p-5">
          <div className="text-xs text-green-400 uppercase font-mono font-black mb-2">Contract Address · Ethereum Mainnet</div>
          <div className="text-white font-mono text-sm break-all mb-3">{CONFIG.ADDRESSES.COIN}</div>
          <a
            href={`https://etherscan.io/token/${CONFIG.ADDRESSES.COIN}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-green-400 text-black font-black text-xs px-4 py-2 border-2 border-black uppercase font-mono hover:bg-yellow-300 transition-colors"
          >
            View on Etherscan <ArrowRight size={12} />
          </a>
        </div>

        {/* HOW TO EARN */}
        <div>
          <h3 className="text-xl font-black uppercase font-mono text-white border-b-4 border-white pb-2 mb-4">
            How to Earn $POGE
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {[
              { n: '01', title: 'Mint an NFT',         desc: '0.005 ETH · Max 10 per wallet · Randomly generated on-chain' },
              { n: '02', title: 'Stake Your NFT',      desc: 'Approve once then stake. No lock-up — unstake anytime, no penalty' },
              { n: '03', title: 'Earn $POGE',          desc: 'Rewards accumulate every second. Claim anytime or auto-claim on unstake' },
              { n: '04', title: 'Build Multiplier',    desc: '1x grows to 2x over 180 days continuous staking. Claim doesn\'t reset it' },
            ].map(({ n, title, desc }) => (
              <div key={n} className="bg-black border-2 border-white/20 border-t-4 border-t-yellow-300 p-5">
                <div className="text-3xl font-black text-yellow-300 font-mono mb-2">{n}</div>
                <div className="text-sm font-black text-white uppercase font-mono mb-2">{title}</div>
                <div className="text-xs text-white/50 font-mono leading-relaxed">{desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* EMISSION TABLE */}
        <div>
          <h3 className="text-xl font-black uppercase font-mono text-white border-b-4 border-white pb-2 mb-4">
            Emission Schedule
          </h3>
          <p className="text-sm text-white/50 font-mono mb-4">
            Rewards decay quadratically over 264 years. Early stakers earn significantly more.
          </p>
          <div className="border-4 border-white overflow-hidden">
            <table className="w-full text-sm font-mono">
              <thead>
                <tr className="bg-yellow-300 text-black">
                  <th className="text-left p-3 font-black uppercase text-xs">Time</th>
                  <th className="text-left p-3 font-black uppercase text-xs">Base Rate / NFT / Day</th>
                  <th className="text-left p-3 font-black uppercase text-xs">With 2x Multiplier</th>
                </tr>
              </thead>
              <tbody>
                {EMISSION_TIMELINE.map(({ day, base, with2x }, i) => (
                  <tr key={day} className={i % 2 === 0 ? 'bg-black' : 'bg-white/5'}>
                    <td className="p-3 text-white/60">{day}</td>
                    <td className="p-3 text-white font-bold">{base}</td>
                    <td className="p-3 text-yellow-300 font-bold">{with2x}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-xs text-white/30 font-mono mt-2">
            Loyalty multiplier grows 1x → 2x over 180 days. Claiming rewards does not reset your multiplier — only unstaking does.
          </div>
        </div>

        {/* GUARANTEES */}
        <div>
          <h3 className="text-xl font-black uppercase font-mono text-white border-b-4 border-white pb-2 mb-4">
            Trustless Guarantees
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {GUARANTEES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-black border border-white/10 border-l-4 border-l-green-400 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={14} className="text-green-400 shrink-0" />
                  <div className="text-xs font-black text-green-400 uppercase font-mono">{title}</div>
                </div>
                <div className="text-xs text-white/50 font-mono leading-relaxed">{desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="bg-yellow-300 border-4 border-black p-8 text-center shadow-[8px_8px_0_0_#000]">
          <div className="text-3xl font-black text-black uppercase font-mono mb-2">
            Start Earning $POGE
          </div>
          <div className="text-sm text-black/60 font-mono mb-6">
            Stake your Dunk Poge NFT and earn from day one · No lock-up · Code is law
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => setView('stake')}
              className="bg-black text-yellow-300 font-black px-8 py-4 border-4 border-black shadow-[4px_4px_0_0_rgba(0,0,0,0.3)] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all uppercase font-mono flex items-center justify-center gap-2"
            >
              Stake Now <ArrowRight size={16} />
            </button>
            <button
              onClick={() => setView('mint')}
              className="bg-white text-black font-black px-8 py-4 border-4 border-black hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all uppercase font-mono"
            >
              Mint an NFT
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};
